import { Type, Static } from '@sinclair/typebox';

export const EmailFolderRoleSchema = Type.Union(
  [
    Type.Literal('TRASH'),
    Type.Literal('INBOX'),
    Type.Literal('SENT'),
    Type.Literal('DRAFTS'),
    Type.Literal('JUNK'),
    Type.Literal('ARCHIVE'),
    Type.Literal('IMPORTANT'),
    Type.Literal('STARRED'),
    Type.Literal('SPAM'),
    Type.Literal('ALL'),
  ],
  {
    description:
      'The folder role represents a supported special-use purpose (based on RFC special-use attributes where applicable); it does not indicate whether the folder is system-created. Gmail can expose system labels beyond the RFC special-use list: these have is_system=true but no role when they do not map to a supported special-use purpose, such as CATEGORY_* labels. For those Gmail labels, use the label ID or name. Outlook supports INBOX, SENT, DRAFTS, TRASH, SPAM, and ARCHIVE for its corresponding default folders; custom folders have no role. [Learn more about roles](https://developer.unipile.com/v2.0/docs/manage-mailboxes#about-roles).',
  },
);

export const EmailFolderSchema = Type.Object({
  object: Type.Literal('EmailFolder'),
  id: Type.String({ description: 'The ID of the folder for the provider.' }),
  name: Type.String({ description: 'The name of the folder.' }),
  role: Type.Optional(EmailFolderRoleSchema),
  total_count: Type.Number({
    description: 'The total number of emails in the folder. For Gmail, the total number of emails assigned with this label.',
  }),
  unread_count: Type.Number({
    description: 'The number of unread emails in the folder. For Gmail, the number of unread emails assigned with this label.',
  }),
  background_color: Type.Optional(Type.String({ description: '(Gmail Only) The background color of the folder.' })),
  text_color: Type.Optional(Type.String({ description: '(Gmail Only) The text color of the folder.' })),
  is_system: Type.Optional(
    Type.Boolean({
      description: '(Gmail Only) If `true`, the label is created by Google. If `false`, the label was created by the user.',
    }),
  ),
  parent_id: Type.Optional(Type.String({ description: '(Outlook only) The ID of the parent folder.' })),
});

export type EmailFolderRole = Static<typeof EmailFolderRoleSchema>;
export type EmailFolder = Static<typeof EmailFolderSchema>;
